import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonImg,
  IonMenuButton,
  IonSearchbar,
  IonToolbar,
} from '@ionic/react';
import {
  chevronBackCircleOutline,
  chevronForwardCircleOutline,
  timeOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useImportProgress } from '../../context/ImportProgressContext';
import { on, removeListener } from '../../helper/electron';
import { useIcons } from '../../hooks/useIcons';
import { RootState } from '../../store/store';
import { AppIdentifiers } from '../../store/slices/token-state.slice';
import GlobalProgressBar from '../globalProgressBar/GlobalProgressBar';
import { readUserLabel } from '../../utils/userLabel';
import './Header.scss';

const GlobalImportProgressPill: React.FC = () => {
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const { getActiveJobs } = useImportProgress();
  const activeJobs = getActiveJobs();
  if (!activeJobs.length) return null;
  const totals = activeJobs.map((j) => j.progress.totalTracks || 0);
  const processeds = activeJobs.map((j) => j.progress.processedTracks || 0);
  const hasAnyProcessing = activeJobs.some(
    (j) => j.progress.phase && j.progress.phase !== 'complete',
  );
  const total = totals.reduce((a, b) => a + b, 0);
  const processed = processeds.reduce((a, b) => a + b, 0);
  let pct = total > 0 ? Math.floor((processed / total) * 100) : 0;
  if (hasAnyProcessing && pct === 100) pct = 99; // avoid visually finishing early during tail work
  const eta = activeJobs.find(
    (j) => (j as any).progress?.estimatedTimeRemaining,
  )?.progress.estimatedTimeRemaining;
  return (
    <>
      <div
        className="global-import-pill"
        title={`Importing… ${pct}%`}
        onClick={() => history.push('/my-library/sources')}
        role="button"
      >
        <IonIcon icon={timeOutline} style={{ marginRight: 6 }} />
        <span>Importing… {pct}%</span>
        {eta && <span className="eta"> · {eta}</span>}
      </div>
      {open && <GlobalImportDetails onClose={() => setOpen(false)} />}
    </>
  );
};

const GlobalImportDetails: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { getActiveJobs } = useImportProgress();
  const activeJobs = getActiveJobs();
  const [confirm, setConfirm] = useState<{
    jobId: string;
    action: 'pause' | 'cancel';
  } | null>(null);
  return (
    <div className="global-import-details-backdrop" onClick={onClose}>
      <div
        className="global-import-details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="details-header">
          <strong>Import Progress</strong>
          <IonButton size="small" fill="clear" onClick={onClose}>
            Close
          </IonButton>
        </div>
        <div className="details-body">
          {activeJobs.map((job) => {
            const total = job.progress.totalTracks || 0;
            const processed = job.progress.processedTracks || 0;
            const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
            return (
              <div key={job.id} className="job-row">
                <div className="job-main">
                  <div className="job-title">{job.name}</div>
                  <div className="job-sub">{job.path}</div>
                </div>
                <div className="job-progress">
                  <div className="bar">
                    <div className="fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="meta">
                    <span>{pct}%</span>
                    {job.progress.estimatedTimeRemaining && (
                      <span> · {job.progress.estimatedTimeRemaining}</span>
                    )}
                    {job.progress.filesPerSecond && (
                      <span> · {job.progress.filesPerSecond} fps</span>
                    )}
                    {(job.progress.created || job.progress.merged) && (
                      <span>
                        {' '}
                        · +{job.progress.created || 0} / upd{' '}
                        {job.progress.merged || 0}
                      </span>
                    )}
                  </div>
                </div>
                <div className="job-actions">
                  <IonButton
                    size="small"
                    color="danger"
                    fill="outline"
                    onClick={() =>
                      setConfirm({ jobId: job.id, action: 'cancel' })
                    }
                  >
                    Cancel
                  </IonButton>
                </div>
              </div>
            );
          })}
        </div>
        {confirm && (
          <div className="confirm-modal" onClick={() => setConfirm(null)}>
            <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-title">Cancel import?</div>
              <div className="confirm-body">
                <p>What should we do with files already imported?</p>
                <div className="confirm-actions">
                  <IonButton
                    size="small"
                    color="danger"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('track-import/cancel', {
                          detail: { id: confirm.jobId, removeImported: true },
                        }),
                      );
                      setConfirm(null);
                    }}
                  >
                    Remove imported
                  </IonButton>
                  <IonButton
                    size="small"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('track-import/cancel', {
                          detail: {
                            id: confirm.jobId,
                            removeImported: false,
                          },
                        }),
                      );
                      setConfirm(null);
                    }}
                  >
                    Keep imported
                  </IonButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface HeaderProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const Header: React.FC<HeaderProps> = ({ searchQuery, setSearchQuery }) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const appToken = useSelector(
    (state: RootState) => state.tokenState[AppIdentifiers.App],
  );
  const userLabel = readUserLabel(appToken?.accessToken ?? null);
  const { icons } = useIcons();
  const handleGoBack = () => {
    history.goBack();
  };

  const handleGoForward = () => {
    history.goForward();
  };

  useEffect(() => {
    on('deep-link', (url: string) => {
      console.log('[ON] deep-link', url);
      if (history) {
        if (url.length == 0) {
          url = '/';
        }
        history.push(url);
      } else {
        console.error('Navigate is not defined.');
      }
    });

    return () => {
      removeListener('deep-link');
    };
  }, [history]);

  return (
    <IonHeader>
      <div className="header-desktop-only">
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonButtons slot="start">
            <IonImg
              src={
                theme === 'dark' ? icons.chLogoDarkMode : icons.chLogoLightMode
              }
              alt="Logo"
              className={`logo ${theme === 'dark' ? 'dark' : ''}`}
            />
          </IonButtons>
          <IonButtons className="navigation-search-container">
            <div className="navigation-container">
              <IonIcon
                className="navigationButton"
                icon={chevronBackCircleOutline}
                onClick={handleGoBack}
              />
              <IonIcon
                className="navigationButton"
                icon={chevronForwardCircleOutline}
                onClick={handleGoForward}
              />
            </div>
            <div className="center">
              <IonSearchbar
                showClearButton="always"
                placeholder="Search songs, pools & DJs."
                className="header-search"
                onIonInput={(e) => setSearchQuery(e.target.value || '')}
                onIonClear={() => setSearchQuery('')}
                onIonCancel={() => setSearchQuery('')}
                onIonChange={(e) => setSearchQuery(e.target.value || '')}
                color="light"
                value={searchQuery}
              />
            </div>
            {/* Spacer to balance layout */}
            <div className="spacer"></div>
          </IonButtons>
          <IonButtons slot="end" className="header-user">
            <GlobalProgressBar className="header-progress" />
            {userLabel && <span className="header-user-name">{userLabel}</span>}
            <img
              className="header-user-avatar"
              src={icons.avatar || ''}
              alt=""
            />
          </IonButtons>
        </IonToolbar>
      </div>

      <div className="header-mobile-only">
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonButtons slot="start">
            <IonImg
              src={
                theme === 'dark' ? icons.chLogoDarkMode : icons.chLogoLightMode
              }
              alt="Logo"
              className={`logo ${theme === 'dark' ? 'dark' : ''}`}
            />
          </IonButtons>
          <IonButtons slot="end" className="mobile-navigation-buttons">
            <IonIcon
              className="navigationButton"
              icon={chevronBackCircleOutline}
              onClick={handleGoBack}
            />
            <IonIcon
              className="navigationButton"
              icon={chevronForwardCircleOutline}
              onClick={handleGoForward}
            />
          </IonButtons>
          <IonButtons slot="end">
            <GlobalProgressBar className="header-progress" />
          </IonButtons>
        </IonToolbar>
        <IonToolbar className="mobile-search-toolbar">
          <IonSearchbar
            showClearButton="always"
            placeholder="Search songs, pools & DJs."
            className="header-search mobile"
            onIonInput={(e) => setSearchQuery(e.target.value || '')}
            onIonClear={() => setSearchQuery('')}
            onIonCancel={() => setSearchQuery('')}
            onIonChange={(e) => setSearchQuery(e.target.value || '')}
            color="light"
            value={searchQuery}
          />
        </IonToolbar>
      </div>
    </IonHeader>
  );
};

export default Header;
